/**
 * KiaanWidget — bottom-right on every AURA web page (spec 2026-10-16, item 1).
 *
 * - Open by default; collapse state in localStorage['kiaan-open'] (try/catch).
 * - ⌘J / Ctrl+J toggles it (moved here from AppShell). `aura-ai-open` events
 *   open it on the Kiaan tab and stash the preset for the embedded AIPanel.
 * - Tabs (SUN-70): ✦ Agents · Communications · Calendar & meetings · System monitor (admins) ·
 *   Dev mode (admins) | Report a bug (others). Access is decided by `useKiaanAccess` (server
 *   permissions, fails closed). The last tab is remembered per user in localStorage (try/catch).
 * - Everything inside tolerates Kiaan Work being down; the AURA page never breaks.
 */
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Map as MapIcon, Minus, StickyNote } from 'lucide-react';
import { usePageNotesAllowed } from '../../modules/mockup-board/page-notes/PageNotesGate';
import { getPageNotesMode, setPageNotesMode, usePageNotesMode } from '../../modules/mockup-board/page-notes/pageNotesMode';
import type { DashboardRoleKey } from '../../pages/dashboard/roleLayouts';
import { useKiaanAccess } from './useKiaanAccess';
import { KiaanTabs, TAB_KEYS, type KiaanTabKey } from './KiaanTabs';
import { KiaanTab } from './KiaanTab';
import { tourAudience } from '../../pages/tour/tourCatalog';
import { CommunicationsTab } from './CommunicationsTab';
import { CalendarMeetingsTab } from './CalendarMeetingsTab';
import { ReportBugTab } from './ReportBugTab';
import { DevMode } from './dev/DevMode';
import { SystemMonitor } from './SystemMonitor';
import { getCodeMapMode, setCodeMapMode, useCodeMapMode } from './codemap/codeMapMode';
import { useAuthStore } from '@/stores/auth';
import './kiaan.css';

// 2026-10-17 (Manak): "Code map" — admin-only, loads only when opened.
const CodeMapDrawer = lazy(() => import('./codemap/CodeMapDrawer').then((m) => ({ default: m.CodeMapDrawer })));

const OPEN_KEY = 'kiaan-open';
const PRESET_KEY = 'aura-ai-preset';

/** SUN-91: a file dropped inside the widget but outside a drop area must not make the browser open it. */
function keepFileInAura(e: React.DragEvent) {
  if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
}

export type KiaanReach = 'unknown' | 'ok' | 'down';

function readOpen(): boolean {
  try {
    const v = window.localStorage.getItem(OPEN_KEY);
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
}

/** The last tab, per user: `kiaan-tab:<userId>`. Anything unknown → Agents. */
export const tabKey = (userId: string | null | undefined) => `kiaan-tab:${userId ?? 'anon'}`;

export function readTab(userId: string | null | undefined): KiaanTabKey {
  try {
    const v = window.localStorage.getItem(tabKey(userId));
    return v && (TAB_KEYS as string[]).includes(v) ? (v as KiaanTabKey) : 'agents';
  } catch {
    return 'agents';
  }
}

function writeTab(userId: string | null | undefined, tab: KiaanTabKey) {
  try {
    window.localStorage.setItem(tabKey(userId), tab);
  } catch {
    /* storage blocked — the widget still works */
  }
}

function writeOpen(open: boolean) {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    /* storage blocked — the widget still works */
  }
}

export interface KiaanWidgetProps {
  role: DashboardRoleKey | null | undefined;
  pathname: string;
  /** The admin's 'View as' choice. Any view except 'owner' shows Kiaan the way that role sees it. */
  previewView?: string | null;
}

export function KiaanWidget({ role, pathname, previewView = null }: KiaanWidgetProps) {
  const realAccess = useKiaanAccess();
  // 'View as' previews a non-admin role: show what that role gets (the server still decides for real users).
  const previewing = !!previewView && previewView !== 'owner';
  const access = previewing
    ? { ...realAccess, devMode: false, manageAgents: false, manageKeys: false, manageKnowledge: false, reportBug: true }
    : realAccess;
  const [open, setOpen] = useState<boolean>(readOpen);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  // SUN-110c: admin/owner (profile P18) sees the waiting requests; same rule as the App Tour's admin audience.
  const isAdminUser = useAuthStore((s) => tourAudience(s.user) === 'admin');
  const [tab, setTabState] = useState<KiaanTabKey>(() => readTab(userId));
  // A user picking a tab: remember it. Fallbacks below (lost access) do not overwrite the memory.
  const setTab = useCallback((t: KiaanTabKey) => { setTabState(t); writeTab(userId, t); }, [userId]);
  // Signed-in user changed (or arrived after the first paint) → their own last tab.
  useEffect(() => { setTabState(readTab(userId)); }, [userId]);
  const [wide, setWide] = useState(false);
  const [reach, setReach] = useState<KiaanReach>('unknown');
  const notesAllowed = usePageNotesAllowed();
  const notesOn = usePageNotesMode();
  const mapWanted = useCodeMapMode();
  // Code map is admin-only, exactly like Dev mode. 'View as' a non-admin role hides it too.
  const mapOn = mapWanted && access.devMode;

  // Lost Dev mode (or started previewing a non-admin role) while the map was open → the map goes away.
  useEffect(() => {
    if (mapWanted && !access.devMode && !realAccess.loading) setCodeMapMode(false);
  }, [mapWanted, access.devMode, realAccess.loading]);

  // Opening Code map ends page notes; Kiaan folds so the page stays visible (same as page notes).
  const openCodeMap = useCallback(() => {
    setPageNotesMode(false);
    setCodeMapMode(true);
    setOpen(false);
    writeOpen(false);
  }, []);
  const closeCodeMap = useCallback(() => setCodeMapMode(false), []);

  // Live work (SUN-55): "Open <screen>" and "See on Code map" go to that AURA screen. pushState + popstate is
  // how the app's router hears about it, so the widget needs no router of its own.
  const goTo = useCallback((route: string) => {
    if (window.location.pathname !== route) {
      window.history.pushState({}, '', route);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, []);
  const openScreenFromLiveWork = useCallback((route: string) => goTo(route), [goTo]);
  const openCodeMapFromLiveWork = useCallback((route: string) => { goTo(route); openCodeMap(); }, [goTo, openCodeMap]);
  // Turning page notes on closes Code map.
  const startPageNotes = useCallback(() => {
    setCodeMapMode(false);
    setPageNotesMode(true);
    setOpen(false);
    writeOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setOpen((o) => {
      writeOpen(!o);
      return !o;
    });
  }, []);
  const collapse = useCallback(() => {
    setOpen(false);
    writeOpen(false);
  }, []);

  // ⌘J / Ctrl+J — kept from AppShell.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== 'j') return;
      e.preventDefault();
      if (getPageNotesMode() || getCodeMapMode()) { setPageNotesMode(false); setCodeMapMode(false); setOpen(true); writeOpen(true); return; }
      toggle();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [toggle]);

  // Pages dispatch CustomEvent('aura-ai-open', {detail: {preset}}) to open the chat.
  useEffect(() => {
    const onAiOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail === 'object') {
        try { window.sessionStorage.setItem(PRESET_KEY, JSON.stringify(detail)); } catch { /* ignore */ }
      }
      setCodeMapMode(false);
      setTab('agents');
      setOpen(true);
      writeOpen(true);
    };
    window.addEventListener('aura-ai-open', onAiOpen as EventListener);
    return () => window.removeEventListener('aura-ai-open', onAiOpen as EventListener);
  }, [setTab]);

  // A user who loses Dev mode (or never had it) must not sit on that tab. Wait for the permissions:
  // while they load every flag is false, and a remembered admin tab must survive that moment.
  // (The body below never renders an admin tab without its flag, loading or not.)
  useEffect(() => {
    if (realAccess.loading) return;
    if ((tab === 'dev' && !access.devMode) || (tab === 'system' && !access.manageAgents) || (tab === 'bug' && !access.reportBug)) {
      setTabState('agents');
    }
  }, [tab, access.devMode, access.reportBug, access.manageAgents, realAccess.loading]);

  // 2026-10-16 (Manak): page notes are for the page behind Kiaan, never Kiaan itself. While notes are on,
  // Kiaan stays folded; clicking K ends notes and opens Kiaan. `data-feedback-toolbar` makes Agentation
  // skip every Kiaan element (it ignores anything inside that marker).
  // 2026-10-17 (Manak): Code map works the same way — a drawer over the page, Kiaan folded, K closes it.
  // Admins get a chip stack above K ("Code map", "Page notes"); everyone else keeps the plain "Stop page notes".
  if (!open || notesOn || mapOn) {
    return (
      <>
        {mapOn && (
          <Suspense fallback={null}>
            <CodeMapDrawer pathname={pathname} onClose={closeCodeMap} />
          </Suspense>
        )}
        <div className="kw-root kw-collapsed" data-aura-chrome="kiaan-widget" data-tour="kiaan-widget" data-feedback-toolbar="kiaan">
          {access.devMode ? (
            <div className="kw-chip-stack">
              <button
                type="button"
                className={`kw-fold-chip${mapOn ? ' kw-on' : ''}`}
                onClick={() => (mapOn ? closeCodeMap() : openCodeMap())}
                title={mapOn ? 'Close the code map' : 'Code map: how this screen works, from the code'}
                aria-label={mapOn ? 'Close Code map' : 'Code map'}
                aria-pressed={mapOn}
              >
                <MapIcon size={14} /> Code map
              </button>
              {notesAllowed && (
                <button
                  type="button"
                  className={`kw-fold-chip${notesOn ? ' kw-on' : ''}`}
                  data-tour="kiaan-page-notes"
                  onClick={() => (notesOn ? setPageNotesMode(false) : startPageNotes())}
                  title={notesOn ? 'Stop page notes' : 'Page notes: point at anything on this page and write a note'}
                  aria-label={notesOn ? 'Stop page notes' : 'Page notes'}
                  aria-pressed={notesOn}
                >
                  <StickyNote size={14} /> Page notes
                </button>
              )}
            </div>
          ) : (
            notesOn && (
              <button type="button" className="kw-notes-stop" onClick={() => setPageNotesMode(false)} title="Stop page notes">
                <StickyNote size={14} /> Stop page notes
              </button>
            )
          )}
          <button
            type="button"
            className="kw-fab"
            onClick={() => {
              if (notesOn || mapOn) { setPageNotesMode(false); setCodeMapMode(false); setOpen(true); writeOpen(true); return; }
              toggle();
            }}
            title={notesOn ? 'Stop page notes and open Kiaan' : mapOn ? 'Close the code map and open Kiaan' : 'Open Kiaan (⌘J)'}
            aria-label="Open Kiaan"
          >
            K
          </button>
        </div>
      </>
    );
  }

  // SUN-70: five tabs need the wide panel on every tab (the narrow one cut the tab row off).
  const panelClass = `kw-panel kw-wide${wide ? ' kw-xwide' : ''}`;

  return (
    <div
      className="kw-root"
      data-aura-chrome="kiaan-widget"
      data-tour="kiaan-widget"
      data-feedback-toolbar="kiaan"
      onDragOver={keepFileInAura}
      onDrop={keepFileInAura}
    >
      <div className={panelClass} role="dialog" aria-label="Kiaan">
        <div className="kw-head">
          <div className="kw-logo" aria-hidden="true">K</div>
          <span className="kw-title">Kiaan</span>
          {tab === 'dev' && access.devMode ? (
            <span className="kw-pill kw-wt">Dev mode · admin</span>
          ) : (
            <span
              className={`kw-dot${reach === 'down' ? ' kw-dot-off' : reach === 'unknown' ? ' kw-dot-busy' : ''}`}
              title={reach === 'ok' ? 'Kiaan Work is running' : reach === 'down' ? 'Kiaan Work is not reachable' : 'Checking Kiaan Work'}
              data-testid="kiaan-reach"
              data-reach={reach}
            />
          )}
          <span className="kw-sp" />
          {notesAllowed && (
            <button
              type="button"
              className={`kw-icon-btn kw-notes-btn${notesOn ? ' kw-on' : ''}`}
              data-tour="kiaan-page-notes"
              onClick={() => (notesOn ? setPageNotesMode(false) : startPageNotes())}
              title={notesOn ? 'Stop page notes' : 'Page notes: point at anything on this page and write a note'}
              aria-label={notesOn ? 'Stop page notes' : 'Page notes'}
              aria-pressed={notesOn}
            >
              <StickyNote size={14} /> <span>Page notes</span>
            </button>
          )}
          {access.devMode && (
            <button
              type="button"
              className="kw-icon-btn kw-codemap-btn"
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={openCodeMap}
              title="Code map: how this screen works, from the code"
              aria-label="Code map"
            >
              <MapIcon size={14} /> <span>Code map</span>
            </button>
          )}
          <span className="kw-kbd">⌘J</span>
          <button type="button" className="kw-icon-btn" onClick={collapse} title="Collapse" aria-label="Collapse Kiaan">
            <Minus size={14} />
          </button>
        </div>

        <KiaanTabs active={tab} devMode={access.devMode} reportBug={access.reportBug} systemMonitor={access.manageAgents} onChange={(t) => { setTab(t); setWide(false); }} />

        <div className="kw-body">
          {tab === 'agents' && (
            <KiaanTab
              role={role}
              pathname={pathname}
              onCollapse={collapse}
              onReach={setReach}
              canManageAgents={access.manageAgents}
              isAdmin={isAdminUser && !previewing}
              canManageKnowledge={access.manageKnowledge}
              onWide={setWide}
              canAnswer={access.devMode}
              onMessageAgent={() => { setTab('comms'); setWide(false); }}
              onOpenScreen={openScreenFromLiveWork}
              onOpenCodeMap={access.devMode ? openCodeMapFromLiveWork : undefined}
            />
          )}
          {tab === 'comms' && <CommunicationsTab isAdmin={access.devMode} canCallAgents={access.manageAgents} />}
          {tab === 'calendar' && <CalendarMeetingsTab />}
          {tab === 'bug' && access.reportBug && <ReportBugTab pathname={pathname} />}
          {tab === 'system' && access.manageAgents && <SystemMonitor />}
          {tab === 'dev' && access.devMode && <DevMode manageKeys={access.manageKeys} manageKnowledge={access.manageKnowledge} />}
        </div>
      </div>
    </div>
  );
}
